import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import {
  getAdjustmentData,
  postAdjustmentData,
} from "../../api/accountAdjustment";
import { customCurrencyFormat } from "../../utils";
import DashboardDropdown from "../dashboard/dashboardDateComponent";
import { useToast } from "@chakra-ui/react";

const DashAdjustment = ({
  customer_id,
  dashDate,
  updateDate,
  dateCategory,
  setDateCategory,
  initialDashDate,
}) => {
  const toast = useToast();
  const formattedDate = format(dashDate, "yyyy-MM-dd");
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const fetchAdjustmentData = async () => {
      try {
        const res = await getAdjustmentData(
          customer_id,
          formattedDate,
          controller.signal
        );
        console.log(res.data.accounts);
        if (isActive && res?.data?.accounts) {
          setAdjustmentData(res.data.accounts);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchAdjustmentData();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate]);

  const handleChange = (idx, field, value) => {
    setAdjustmentData((prev) => {
      const newData = [...prev];

      if (field === "remove") {
        newData[idx][field] = !newData[idx][field];
      } else {
        const numericValue = value === "" ? "" : parseFloat(value) || 0;

        if (numericValue !== "" && numericValue !== 0) {
          ["override", "add", "subtract"]
            .filter((f) => f !== field)
            .forEach((f) => {
              newData[idx][f] = 0;
            });
        }

        newData[idx][field] = numericValue;
      }

      newData[idx].adjusted_balance = computeAdjustedTotal(newData[idx]);

      return newData;
    });
  };

  const computeAdjustedTotal = (row) => {
    if (row.remove) return 0;
    if (row.override) return row.override;
    return (row.total || 0) + (row.add || 0) - (row.subtract || 0);
  };

  const groupedAccounts = useMemo(() => {
    const groups = {};
    adjustmentData.forEach((row, idx) => {
      const category = row.category || "Other";
      if (!groups[category]) groups[category] = [];
      groups[category].push({ ...row, idx });
    });
    return groups;
  }, [adjustmentData]);

  const sectionTotals = useMemo(() => {
    const totals = {};
    Object.entries(groupedAccounts).forEach(([category, rows]) => {
      totals[category] = rows.reduce(
        (sum, r) => sum + computeAdjustedTotal(adjustmentData[r.idx]),
        0
      );
    });
    return totals;
  }, [groupedAccounts, adjustmentData]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await postAdjustmentData(customer_id, adjustmentData, formattedDate);
      toast({
        title: "Adjustments saved.",
        description: "Your account adjustments have been successfully saved.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Save failed.",
        description: "Failed to save adjustments. Please try again.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box
      overflowX="auto"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      bgColor="white"
    >
      <Box height="60px" bg="#1A202C" px={4}>
        <Flex justify="flex-end" align="center" height="100%" gap={3}>
          <DashboardDropdown
            key={dashDate?.toISOString()}
            height="36px"
            setEnd={updateDate}
            dateFromNav={initialDashDate}
            endDate={dashDate}
            category={dateCategory}
            setCategory={setDateCategory}
            monthAmount={12}
          />
          <Button colorScheme="blue" onClick={handleSave} isLoading={loading}>
            Save Adjustments
          </Button>
        </Flex>
      </Box>
      <Table variant="simple" size="sm" colorScheme="blue">
        <Thead>
          <Tr height="40px">
            <Th>Account Name</Th>
            <Th textAlign="center">Current Month</Th>
            <Th textAlign="center">Remove</Th>
            <Th textAlign="center">Override</Th>
            <Th textAlign="center">Add</Th>
            <Th textAlign="center">Subtract</Th>
            <Th textAlign="right">Adjusted Total</Th>
          </Tr>
        </Thead>
        <Tbody>
          {Object.entries(groupedAccounts).map(([category, accounts]) => (
            <React.Fragment key={category}>
              <Tr bg="blue.200">
                <Td colSpan={7} fontWeight="bold">
                  {category.toUpperCase()}
                </Td>
              </Tr>

              {accounts.map((row) => {
                const adjustedTotal = computeAdjustedTotal(
                  adjustmentData[row.idx]
                );
                return (
                  <Tr key={row.idx} _hover={{ bg: "blue.50" }}>
                    <Td fontWeight="medium">{row.account_name}</Td>
                    <Td textAlign="center">
                      {customCurrencyFormat(
                        row.total || 0,
                        false,
                        false,
                        true,
                        true,
                        true
                      )}
                    </Td>
                    <Td textAlign="center">
                      <Checkbox
                        isChecked={row.remove}
                        onChange={() => handleChange(row.idx, "remove")}
                      />
                    </Td>
                    <Td textAlign="center">
                      <Input
                        size="sm"
                        type="number"
                        value={row.override === "" ? "" : row.override}
                        onFocus={() => {
                          if (row.override === 0)
                            handleChange(row.idx, "override", "");
                        }}
                        onChange={(e) =>
                          handleChange(row.idx, "override", e.target.value)
                        }
                        onBlur={() => {
                          if (row.override === "")
                            handleChange(row.idx, "override", 0);
                        }}
                        variant="flushed"
                      />
                    </Td>

                    <Td textAlign="center">
                      <Input
                        size="sm"
                        type="number"
                        value={row.add === "" ? "" : row.add}
                        onFocus={() => {
                          if (row.add === 0) handleChange(row.idx, "add", "");
                        }}
                        onChange={(e) =>
                          handleChange(row.idx, "add", e.target.value)
                        }
                        onBlur={() => {
                          if (row.add === "") handleChange(row.idx, "add", 0);
                        }}
                        variant="flushed"
                      />
                    </Td>

                    <Td textAlign="center">
                      <Input
                        size="sm"
                        type="number"
                        value={row.subtract === "" ? "" : row.subtract}
                        onFocus={() => {
                          if (row.subtract === 0)
                            handleChange(row.idx, "subtract", "");
                        }}
                        onChange={(e) =>
                          handleChange(row.idx, "subtract", e.target.value)
                        }
                        onBlur={() => {
                          if (row.subtract === "")
                            handleChange(row.idx, "subtract", 0);
                        }}
                        variant="flushed"
                      />
                    </Td>
                    <Td textAlign="right" fontWeight="semibold">
                      {customCurrencyFormat(
                        adjustedTotal,
                        false,
                        false,
                        true,
                        true,
                        true
                      )}
                    </Td>
                  </Tr>
                );
              })}

              {/* Subtotal Row */}
              <Tr bg="blue.100">
                <Td fontWeight="bold">Subtotal</Td>
                <Td colSpan={6} textAlign="right" fontWeight="bold">
                  {customCurrencyFormat(
                    sectionTotals[category],
                    false,
                    false,
                    true,
                    true,
                    true
                  )}
                </Td>
              </Tr>
            </React.Fragment>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default DashAdjustment;
